/**
 * Shared Button Component
 * Following Feature-Based Modular Architecture and SOLID principles
 * Reusable button component with consistent styling and behavior
 */

import React, { useCallback } from 'react'

import { ButtonProps } from '@/shared/types/common.types'

const Button = React.memo<ButtonProps>(
  ({
    children,
    variant = 'primary',
    size = 'md',
    disabled = false,
    loading = false,
    onClick,
    type = 'button',
    className = '',
    testId,
    ...props
  }) => {
    // Base button classes
    const baseClasses = [
      'inline-flex',
      'items-center',
      'justify-center',
      'font-medium',
      'rounded-md',
      'transition-all',
      'duration-200',
      'focus:outline-none',
      'focus:ring-2',
      'focus:ring-offset-2',
      'disabled:opacity-50',
      'disabled:cursor-not-allowed',
      'disabled:pointer-events-none',
    ]

    // Size variants
    const sizeClasses = {
      sm: ['px-3', 'py-1.5', 'text-sm'],
      md: ['px-4', 'py-2', 'text-sm'],
      lg: ['px-6', 'py-3', 'text-base'],
    }

    // Style variants
    const variantClasses = {
      primary: [
        'bg-crypto-primary',
        'text-white',
        'border-transparent',
        'hover:bg-crypto-primary/90',
        'focus:ring-crypto-primary',
        'shadow-sm',
      ],
      secondary: [
        'bg-gray-100',
        'dark:bg-crypto-card',
        'text-gray-900',
        'dark:text-crypto-text',
        'border',
        'border-gray-300',
        'dark:border-crypto-border',
        'hover:bg-gray-200',
        'dark:hover:bg-crypto-card/80',
        'focus:ring-gray-300',
      ],
      danger: [
        'bg-red-600',
        'text-white',
        'border-transparent',
        'hover:bg-red-700',
        'focus:ring-red-500',
        'shadow-sm',
      ],
      ghost: [
        'bg-transparent',
        'text-gray-700',
        'dark:text-crypto-text',
        'border',
        'border-transparent',
        'hover:bg-gray-100',
        'dark:hover:bg-crypto-card/50',
        'focus:ring-gray-300',
      ],
      outline: [
        'bg-transparent',
        'text-gray-700',
        'dark:text-crypto-text',
        'border',
        'border-gray-300',
        'dark:border-crypto-border',
        'hover:bg-gray-50',
        'dark:hover:bg-crypto-card/50',
        'focus:ring-gray-300',
      ],
    }

    // Combine all classes
    const buttonClasses = [
      ...baseClasses,
      ...sizeClasses[size],
      ...variantClasses[variant],
      className,
    ]
      .filter(Boolean)
      .join(' ')

    const handleClick = useCallback(() => {
      if (!disabled && !loading && onClick) {
        onClick()
      }
    }, [disabled, loading, onClick])

    return (
      <button
        type={type}
        className={buttonClasses}
        disabled={disabled || loading}
        onClick={handleClick}
        data-testid={testId}
        {...props}
      >
        {loading && (
          <svg
            className="animate-spin -ml-1 mr-2 h-4 w-4"
            xmlns="http://www.w3.org/2000/svg"
            fill="none"
            viewBox="0 0 24 24"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
            />
          </svg>
        )}
        {children}
      </button>
    )
  },
)

Button.displayName = 'Button'

export default Button
